/**
 * The Manage-access drawer's logic, kept apart from the screen so it can be tested on its own
 * (SC-439).
 *
 * The draft is the set of permissions the member should end up holding. Each permission is shown
 * against the role's defaults as one of three states: `role-default` (the draft matches the
 * role), `allowed` (held although the role doesn't give it) or `denied` (not held although the
 * role gives it). The save turns that into the smallest patch: only the keys whose override has
 * to change, so unrelated overrides are never rewritten.
 *
 * Only the server decides who may change what; the locks here repeat its answer (`canEditRole`,
 * `canEditPermissions`, `lockedKeys`) so the screen says why before the person tries.
 */
import {
  OWNER_ROLE_KEY,
  PERMISSION_CATALOGUE,
  PERMISSION_GROUPS,
} from '../../../types/rbac/catalogue'
import { canAssignRole, effectivePermissions } from '../../../types/rbac/evaluate'
import type {
  MemberAccess,
  MemberAccessPatch,
  RbacCatalogue,
  RbacRole,
} from '../services/rbacService'
import { needPermissionMessage, permissionLabel, refusalMessage } from './rbacMessages'

export type KeyState = 'role-default' | 'allowed' | 'denied'

export interface AccessDraft {
  /** The role the member will have after saving. */
  roleKey: string | null
  /** The permissions the member should hold after saving. */
  granted: ReadonlySet<string>
}

export interface PickerPermission {
  key: string
  group: string
  label: string
  description: string
  grantable: boolean
  reserved: boolean
  isActive: boolean
}

/** The catalogue from the API when it has arrived, else the copy compiled into this bundle. */
export function pickerPermissions(catalogue: RbacCatalogue | null): PickerPermission[] {
  if (catalogue) {
    return catalogue.permissions.map((p) => ({
      key: p.key,
      group: p.group,
      label: p.label,
      description: p.description,
      grantable: p.grantable && !p.ownerOnly,
      reserved: p.reserved,
      isActive: p.isActive,
    }))
  }
  return PERMISSION_CATALOGUE.map((p) => ({
    key: p.key,
    group: p.group,
    label: p.label,
    description: p.description,
    grantable: p.grantable,
    reserved: p.reserved,
    isActive: true,
  }))
}

export function pickerGroups(catalogue: RbacCatalogue | null): string[] {
  return catalogue?.groups?.length ? catalogue.groups : [...PERMISSION_GROUPS]
}

export function startDraft(access: MemberAccess): AccessDraft {
  return { roleKey: access.roleKey, granted: new Set(access.effective) }
}

/**
 * What a role gives on its own. The member's current role uses the server's answer; another
 * role is worked out from the roles list with the evaluator's rules (owner-only and retired keys
 * never count).
 */
export function roleDefaultsFor(
  roleKey: string | null,
  access: MemberAccess,
  roles: readonly RbacRole[],
  catalogue: RbacCatalogue | null
): Set<string> {
  if (roleKey === access.roleKey) return new Set(access.roleDefaults)
  const role = roles.find((r) => r.key === roleKey)
  if (!role || !roleKey) return new Set()
  const retiredKeys = pickerPermissions(catalogue)
    .filter((p) => !p.isActive)
    .map((p) => p.key)
  return new Set(effectivePermissions({ roleKey, rolePermissions: role.permissions, retiredKeys }))
}

export function keyState(key: string, draft: AccessDraft, defaults: ReadonlySet<string>): KeyState {
  const held = draft.granted.has(key)
  if (held === defaults.has(key)) return 'role-default'
  return held ? 'allowed' : 'denied'
}

function withGranted(draft: AccessDraft, mutate: (granted: Set<string>) => void): AccessDraft {
  const granted = new Set(draft.granted)
  mutate(granted)
  return { ...draft, granted }
}

export type IsLocked = (key: string) => boolean

export function toggleKey(draft: AccessDraft, key: string, isLocked: IsLocked): AccessDraft {
  if (isLocked(key)) return draft
  return withGranted(draft, (granted) => {
    if (!granted.delete(key)) granted.add(key)
  })
}

/** Select all, clear all, or a group's box: only the keys that can be changed move. */
export function setKeys(
  draft: AccessDraft,
  keys: readonly string[],
  on: boolean,
  isLocked: IsLocked
): AccessDraft {
  return withGranted(draft, (granted) => {
    for (const key of keys) {
      if (isLocked(key)) continue
      if (on) granted.add(key)
      else granted.delete(key)
    }
  })
}

/** Back to what the role gives, for these keys. Locked keys stay as they are. */
export function resetKeys(
  draft: AccessDraft,
  keys: readonly string[],
  defaults: ReadonlySet<string>,
  isLocked: IsLocked
): AccessDraft {
  return withGranted(draft, (granted) => {
    for (const key of keys) {
      if (isLocked(key)) continue
      if (defaults.has(key)) granted.add(key)
      else granted.delete(key)
    }
  })
}

/** How far the draft is from the role's defaults, counted over the keys that can be held. */
export function countChanges(
  draft: AccessDraft,
  defaults: ReadonlySet<string>,
  keys: readonly string[]
): { added: number; removed: number } {
  let added = 0
  let removed = 0
  for (const key of keys) {
    const state = keyState(key, draft, defaults)
    if (state === 'allowed') added += 1
    if (state === 'denied') removed += 1
  }
  return { added, removed }
}

/**
 * Switching role. `keep` leaves the member's permissions as drafted, so the new role's defaults
 * show the difference; `defaults` takes the new role's own permissions (except keys that are
 * locked, which stay as they are).
 */
export function switchRole(
  draft: AccessDraft,
  roleKey: string,
  newDefaults: ReadonlySet<string>,
  mode: 'keep' | 'defaults',
  isLocked: IsLocked,
  keys: readonly string[]
): AccessDraft {
  const switched: AccessDraft = { ...draft, roleKey }
  return mode === 'keep' ? switched : resetKeys(switched, keys, newDefaults, isLocked)
}

/**
 * Switching role when single permissions can't be edited (no `permissions.manage`). The member's
 * overrides stay as they are on the server, so the draft shows what they will really hold: the
 * new role's defaults, plus their allows, minus their denies. Locks don't apply: nothing here is
 * a permission edit.
 */
export function switchRoleOnly(
  draft: AccessDraft,
  roleKey: string,
  newDefaults: ReadonlySet<string>,
  overrides: ReadonlyArray<{ key: string; isAllowed: boolean }>
): AccessDraft {
  const granted = new Set(newDefaults)
  for (const o of overrides) if (o.isAllowed) granted.add(o.key)
  for (const o of overrides) if (!o.isAllowed) granted.delete(o.key)
  return { ...draft, roleKey, granted }
}

/**
 * The smallest PUT body that turns what the server holds into the draft, or null when there is
 * nothing to save. A key is listed only when its override has to change: an unchanged role sends
 * only the keys the person touched, and a changed role also clears overrides the new role makes
 * redundant. `isAllowed: null` clears an override.
 *
 * Without `permissions.manage` (`canEditPermissions` false) any changed override is refused by the
 * server, so only the role is sent.
 */
export function buildAccessPatch(
  access: MemberAccess,
  draft: AccessDraft,
  defaults: ReadonlySet<string>,
  keys: readonly string[],
  canEditPermissions: boolean = true
): MemberAccessPatch | null {
  const roleChanged = draft.roleKey !== null && draft.roleKey !== access.roleKey
  if (!canEditPermissions) return roleChanged ? { roleKey: draft.roleKey as string } : null
  const current = new Map(access.overrides.map((o) => [o.key, o.isAllowed] as const))
  const before = new Set(access.effective)

  const overrides: Array<{ key: string; isAllowed: boolean | null }> = []
  for (const key of keys) {
    const held = draft.granted.has(key)
    const target = held === defaults.has(key) ? null : held
    const now = current.has(key) ? (current.get(key) as boolean) : null
    const changed = roleChanged ? target !== now : held !== before.has(key)
    if (changed) overrides.push({ key, isAllowed: target })
  }

  const patch: MemberAccessPatch = {}
  if (roleChanged) patch.roleKey = draft.roleKey as string
  if (overrides.length > 0) patch.overrides = overrides
  return patch.roleKey === undefined && patch.overrides === undefined ? null : patch
}

export interface LockContext {
  /** What the person making the change holds. */
  actorPermissions: readonly string[]
  isSelf: boolean
  catalogue: RbacCatalogue | null
  /** The company's roles as the API names them; the system roles of the catalogue stand in. */
  roles?: readonly RbacRole[]
}

/** The name the API gives the Owner role: a platform admin can rename it, so it is never typed here. */
function ownerRoleName(ctx: Pick<LockContext, 'roles' | 'catalogue'>): string | undefined {
  return (
    ctx.roles?.find((r) => r.key === OWNER_ROLE_KEY)?.name ??
    ctx.catalogue?.systemRoles.find((r) => r.key === OWNER_ROLE_KEY)?.name
  )
}

/**
 * Why a kind of edit is locked, or null when it is not. Mirrors the order the server judges in:
 * the permission to make this kind of edit, then who the member is, then what the member holds.
 */
export function accessLockMessage(
  kind: 'role' | 'permissions',
  access: MemberAccess,
  ctx: LockContext
): string | null {
  const allowed = kind === 'role' ? access.canEditRole : access.canEditPermissions
  if (allowed) return null

  const needed = kind === 'role' ? 'roles.assign' : 'permissions.manage'
  if (!ctx.actorPermissions.includes(needed)) return needPermissionMessage(needed, ctx.catalogue)
  if (ctx.isSelf) return refusalMessage('self_edit', undefined, ctx.catalogue)
  if (access.roleKey === OWNER_ROLE_KEY)
    return refusalMessage('owner_target', undefined, ctx.catalogue)

  // The first thing the member holds that the actor does not, in catalogue order, as the server finds it.
  const beyond = PERMISSION_CATALOGUE.find(
    (p) => access.effective.includes(p.key) && !ctx.actorPermissions.includes(p.key)
  )
  if (beyond) return refusalMessage('target_holds_more', beyond.key, ctx.catalogue)
  return refusalMessage(access.reasonIfLocked, undefined, ctx.catalogue)
}

/** Why one permission row can't be switched, or null when it can. */
export function rowLockMessage(
  permission: PickerPermission,
  access: MemberAccess,
  ctx: LockContext
): string | null {
  if (!permission.grantable) {
    const name = ownerRoleName(ctx)
    return `${name ? `Reserved for the ${name} role` : "Reserved for the workspace's owning role"}. It can't be given through a role or a single permission.`
  }
  if (!permission.isActive) return 'This permission has been retired.'
  const whole = accessLockMessage('permissions', access, ctx)
  if (whole) return whole
  if (access.lockedKeys.includes(permission.key)) {
    return `You can't give ${permissionLabel(permission.key, ctx.catalogue)} permission, which you don't have.`
  }
  return null
}

export interface RoleOption {
  key: string
  name: string
  description: string
  isSystem: boolean
  /** Why this role can't be picked, or null. */
  disabledReason: string | null
}

/**
 * Roles the person may pick. Owner is never offered (a platform admin manages Owners), and a role
 * that adds anything the person doesn't hold is disabled, as the server would refuse it.
 */
export function roleOptions(
  access: MemberAccess,
  roles: readonly RbacRole[],
  actorPermissions: readonly string[],
  catalogue: RbacCatalogue | null
): RoleOption[] {
  const retiredKeys = pickerPermissions(catalogue)
    .filter((p) => !p.isActive)
    .map((p) => p.key)
  const options = roles
    .filter((role) => role.key !== OWNER_ROLE_KEY)
    .map<RoleOption>((role) => ({
      key: role.key,
      name: role.name,
      description: role.description,
      isSystem: role.isSystem,
      disabledReason:
        role.key === access.roleKey ||
        canAssignRole(
          actorPermissions,
          { key: role.key, permissions: role.permissions },
          retiredKeys
        )
          ? null
          : "Includes permissions you don't have",
    }))
  if (access.roleKey && !options.some((o) => o.key === access.roleKey)) {
    // The Owner, or a role the list doesn't carry: shown so the select has a value, not pickable.
    // The name is the API's (the list, else the catalogue's system roles): a platform admin can
    // rename Owner. With neither, the key is shown rather than a name this bundle made up.
    const known = roles.find((r) => r.key === access.roleKey)
    const systemName = catalogue?.systemRoles.find((r) => r.key === access.roleKey)?.name
    options.unshift({
      key: access.roleKey,
      name: known?.name ?? systemName ?? access.roleKey,
      description: known?.description ?? '',
      isSystem: known?.isSystem ?? access.roleKey === OWNER_ROLE_KEY,
      disabledReason: `The ${known?.name ?? systemName ?? access.roleKey} role is managed by a platform admin`,
    })
  }
  return options
}
